import { z } from 'zod';
import { learnerPathSchema } from './placement.js';
import { capabilityLevelSchema } from './program.js';
import { workshopSpecSchema } from './workshop.js';
/**
 * Authoring contracts for curriculum content. The content package is validated
 * against these before it is synced into Postgres.
 */
export const activityKindSchema = z.enum([
    'lesson',
    'workshop',
    'field_beat',
    'checkpoint',
    'field_day',
]);
export const capabilityGroupSchema = z.enum([
    'model_foundations',
    'retrieval_and_knowledge',
    'agents_and_tools',
    'evaluation_discipline',
    'production_and_security',
    'ai_assisted_engineering',
    'forward_deployed_craft',
]);
export const contentCapabilitySchema = z.object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    group: capabilityGroupSchema,
    title: z.string().min(1),
    isEdge: z.boolean(),
    weekIntroduced: z.number().int().min(1),
    weekIndependent: z.number().int().min(1),
    evidence: z.string().min(1),
});
export const contentReadingSchema = z.object({
    title: z.string().min(1),
    author: z.string().optional(),
    url: z.url().optional(),
    note: z.string().optional(),
});
/** A comprehension check at the end of a lesson. The answer is revealed on demand, never shown by default. */
export const contentCheckSchema = z.object({
    question: z.string().min(1),
    answer: z.string().min(1),
});
/**
 * How a workshop is worked in the browser IDE.
 * manual: the learner writes the code, the coding agent is disabled.
 * agent_directed: the learner writes a brief and directs the coding agent, then reviews.
 * mixed: the body names which steps are manual and which are agent-directed.
 */
export const workshopModeSchema = z.enum(['manual', 'agent_directed', 'mixed']);
/** Structured workshop specification. Hints and mentor notes never reach the learner-facing read models. */
export const contentWorkshopSchema = z.object({
    mode: workshopModeSchema,
    /** Starter project directory under content/workshops, e.g. "w1-1-answerer-v0". */
    starter: z.string().regex(/^[a-z0-9-]+$/),
    /** What the learner must be able to do afterwards. Three to six items. */
    objectives: z.array(z.string().min(1)).min(1),
    /** Markdown: the eval cases and graders this workshop is judged by. */
    evalCases: z.string().min(1),
    /** Four-level hint ladder: direction, conceptual hint, small example, guided recovery. */
    hints: z.array(z.string().min(1)).length(4),
    /** Markdown: the delegation brief the learner is expected to write, for agent-directed steps. */
    briefTemplate: z.string().optional(),
    /** Markdown, hidden from learners: what Mio watches for, common wrong turns, a solution outline. */
    mentorNotes: z.string().min(1),
});
/** The learner-facing part of a workshop spec. Hints and mentor notes are served only to Mio. */
export const workshopPublicSchema = z.object({
    mode: workshopModeSchema,
    starter: z.string(),
    objectives: z.array(z.string()),
    evalCases: z.string(),
    briefTemplate: z.string().nullable(),
});
export const contentActivitySchema = z.object({
    code: z.string().min(1),
    kind: activityKindSchema,
    day: z.number().int().min(1),
    title: z.string().min(1),
    summary: z.string().min(1),
    /** Markdown. Lessons render this as the learning document until richer content lands. */
    body: z.string().min(1),
    acceptance: z.array(z.string().min(1)).default([]),
    capabilities: z.array(z.string()).default([]),
    estimatedMinutes: z.number().int().positive().optional(),
    /** Optional assessment data; the active curriculum currently publishes no checks. */
    checks: z.array(contentCheckSchema).default([]),
    /** Teaching brief for a workshop; executable task pins use workshopRuntime. */
    workshop: contentWorkshopSchema.optional(),
    /** Internal executable task pin. Published through the separate workshop runtime API. */
    workshopRuntime: workshopSpecSchema.optional(),
});
export const contentWeekSchema = z.object({
    number: z.number().int().min(1),
    arc: z.number().int().min(1),
    title: z.string().min(1),
    outcome: z.string().min(1),
    whyThisWeek: z.string().optional(),
    fieldThread: z.string().min(1),
    mioStance: z.string().min(1),
    edgeDrills: z.array(z.string()).default([]),
    readings: z.array(contentReadingSchema).default([]),
    activities: z.array(contentActivitySchema).min(1),
});
/**
 * Who a module is for, so a learner who takes only some modules can place themselves.
 * Rendered on the module brief and in the placement questionnaire result; never a gate.
 */
export const modulePlacementSchema = z.object({
    audience: z.string().min(1),
    prerequisites: z.array(z.string().min(1)).default([]),
    skipWhen: z.string().min(1),
});
/** Curriculum sections can span any positive number of planned learning days. */
export const contentModuleSchema = z
    .object({
    number: z.number().int().positive(),
    durationDays: z.number().int().positive(),
    title: z.string().min(1),
    outcome: z.string().min(1),
    whyThisModule: z.string().optional(),
    placement: modulePlacementSchema.optional(),
    topics: z.array(z.string().min(1)).default([]),
    fieldThread: z.string().min(1),
    mioStance: z.string().min(1),
    edgeDrills: z.array(z.string()).default([]),
    readings: z.array(contentReadingSchema).default([]),
    activities: z.array(contentActivitySchema).min(1),
})
    .superRefine((module, ctx) => {
    module.activities.forEach((activity, index) => {
        if (activity.day > module.durationDays)
            ctx.addIssue({
                code: 'custom',
                path: ['activities', index, 'day'],
                message: 'Activity day exceeds the module duration',
            });
    });
});
export const contentArcSchema = z.object({
    number: z.number().int().min(1),
    title: z.string().min(1),
    weekStart: z.number().int().min(1),
    weekEnd: z.number().int().min(1),
    outcome: z.string().min(1),
    topics: z.array(z.string().min(1)),
});
export const contentEdgePracticeSchema = z.object({
    rank: z.number().int().min(1),
    title: z.string().min(1),
    why: z.string().min(1),
    drill: z.string().min(1),
});
export const contentProgramSchema = z
    .object({
    slug: z.string().regex(/^[a-z0-9-]+$/),
    title: z.string().min(1),
    description: z.string().min(1),
    totalWeeks: z.number().int().min(1),
    hoursPerDay: z.tuple([z.number(), z.number()]),
    /** Flexible modules allow a workshop to span its suggested starting day. */
    pacing: z.enum(['daily-budget', 'flexible']).optional(),
    principles: z.array(z.object({ title: z.string(), body: z.string() })),
    modules: z.array(contentModuleSchema).min(1),
    capabilities: z.array(contentCapabilitySchema).min(1),
})
    .superRefine((program, ctx) => {
    const weeks = Math.ceil(program.modules.reduce((sum, module) => sum + module.durationDays, 0) / 5);
    if (program.totalWeeks !== weeks)
        ctx.addIssue({
            code: 'custom',
            path: ['totalWeeks'],
            message: `Expected ${weeks} planned weeks from module durations`,
        });
    const slugs = new Set(program.capabilities.map((c) => c.slug));
    const codes = new Set();
    const numbers = new Set();
    for (const module of program.modules) {
        if (numbers.has(module.number))
            ctx.addIssue({ code: 'custom', message: `duplicate module number ${module.number}` });
        numbers.add(module.number);
        for (const activity of module.activities) {
            if (codes.has(activity.code)) {
                ctx.addIssue({ code: 'custom', message: `duplicate activity code ${activity.code}` });
            }
            codes.add(activity.code);
            if (activity.kind !== 'workshop' && activity.workshop) {
                ctx.addIssue({
                    code: 'custom',
                    message: `activity ${activity.code} is not a workshop but has a workshop specification`,
                });
            }
            if (activity.kind !== 'workshop' && activity.workshopRuntime) {
                ctx.addIssue({
                    code: 'custom',
                    message: `activity ${activity.code} is not a workshop but has an executable workshop specification`,
                });
            }
            for (const slug of activity.capabilities) {
                if (!slugs.has(slug)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: `activity ${activity.code} references unknown capability ${slug}`,
                    });
                }
            }
        }
    }
});
/* ---------- API read models ---------- */
export const activitySummarySchema = z.object({
    id: z.uuid(),
    code: z.string(),
    kind: activityKindSchema,
    day: z.number().int(),
    title: z.string(),
    summary: z.string(),
    estimatedMinutes: z.number().int().nullable(),
    status: z.enum(['not_started', 'in_progress', 'complete']),
});
export const activityDetailSchema = activitySummarySchema.extend({
    body: z.string(),
    acceptance: z.array(z.string()),
    checks: z.array(contentCheckSchema),
    workshop: workshopPublicSchema.nullable(),
    capabilities: z.array(z.object({ slug: z.string(), title: z.string(), isEdge: z.boolean() })),
    module: z.object({ number: z.number().int(), title: z.string() }),
    previous: z.object({ id: z.uuid(), code: z.string(), title: z.string() }).nullable(),
    next: z.object({ id: z.uuid(), code: z.string(), title: z.string() }).nullable(),
});
export const moduleSummarySchema = z.object({
    number: z.number().int(),
    durationDays: z.number().int().positive(),
    topics: z.array(z.string()),
    title: z.string(),
    outcome: z.string(),
    activityCount: z.number().int(),
    completedCount: z.number().int(),
    status: z.enum(['locked', 'current', 'complete', 'upcoming']),
});
export const moduleDetailSchema = moduleSummarySchema.extend({
    whyThisModule: z.string().nullable(),
    placement: modulePlacementSchema.nullable(),
    fieldThread: z.string(),
    mioStance: z.string(),
    edgeDrills: z.array(z.string()),
    readings: z.array(contentReadingSchema),
    days: z.array(z.object({
        day: z.number().int(),
        activities: z.array(activitySummarySchema),
    })),
});
export const capabilityStateSchema = z.object({
    slug: z.string(),
    group: capabilityGroupSchema,
    title: z.string(),
    isEdge: z.boolean(),
    weekIntroduced: z.number().int(),
    weekIndependent: z.number().int(),
    evidence: z.string(),
    level: capabilityLevelSchema,
});
export const programOverviewSchema = z.object({
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    totalWeeks: z.number().int(),
    hoursPerDay: z.tuple([z.number(), z.number()]),
    principles: z.array(z.object({ title: z.string(), body: z.string() })),
    modules: z.array(moduleSummarySchema),
    currentModule: z.number().int(),
    completionPercent: z.number(),
});
export const progressUpdateSchema = z.object({
    status: z.enum(['in_progress', 'complete']),
});
/* ---------- Whole-program read model: one request, everything the app needs ---------- */
export const fullActivitySchema = z.object({
    id: z.uuid(),
    code: z.string(),
    kind: activityKindSchema,
    day: z.number().int(),
    title: z.string(),
    summary: z.string(),
    body: z.string(),
    acceptance: z.array(z.string()),
    estimatedMinutes: z.number().int().nullable(),
    capabilities: z.array(z.string()),
    checks: z.array(contentCheckSchema),
    workshop: workshopPublicSchema.nullable(),
});
export const fullModuleSchema = z.object({
    number: z.number().int(),
    durationDays: z.number().int().positive(),
    topics: z.array(z.string()),
    title: z.string(),
    outcome: z.string(),
    whyThisModule: z.string().nullable(),
    placement: modulePlacementSchema.nullable(),
    fieldThread: z.string(),
    mioStance: z.string(),
    edgeDrills: z.array(z.string()),
    readings: z.array(contentReadingSchema),
    /** Canonical order: the order activities are taken in. */
    activities: z.array(fullActivitySchema),
});
export const programFullSchema = z.object({
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    totalWeeks: z.number().int(),
    hoursPerDay: z.tuple([z.number(), z.number()]),
    principles: z.array(z.object({ title: z.string(), body: z.string() })),
    modules: z.array(fullModuleSchema),
    capabilities: z.array(contentCapabilitySchema),
    contentVersion: z.string(),
    /** activityId -> status, only for activities the learner has touched. */
    progress: z.record(z.string(), z.enum(['in_progress', 'complete'])),
    /** The learner's saved path from the placement questionnaire, when they have one. */
    path: learnerPathSchema.nullable(),
});
/** Five learning days form a planning week; modules need not last one week. */
export function plannedWeekOf(modules, moduleNumber, day = 1) {
    const offset = modules
        .filter((module) => module.number < moduleNumber)
        .reduce((sum, module) => sum + module.durationDays, 0);
    return Math.ceil((offset + day) / 5);
}
